import { apiFetch } from "./apiClient";
import { foodsClient } from '../../../backend/src/client';
import type { Food as BackendFood } from '@macro-tracker/models/validators';
import type { FoodItem, FoodSearchResult } from '@macro-tracker/models';
const client = foodsClient(apiFetch);

const toNumber = (value: number | null | undefined): number =>
  typeof value === "number" && Number.isFinite(value) ? value : 0;

const extractLabelNutrients = (
  nutrition: unknown
): FoodItem['servingSizes'][number]['nutrition']['labelNutrients'] => {
  if (!nutrition || typeof nutrition !== 'object') {
    return undefined;
  }

  const maybeLabelNutrients = (nutrition as { labelNutrients?: unknown }).labelNutrients;
  if (!maybeLabelNutrients || typeof maybeLabelNutrients !== 'object') {
    return undefined;
  }

  return maybeLabelNutrients as FoodItem['servingSizes'][number]['nutrition']['labelNutrients'];
};

export const isBackendFoodId = (id: string): boolean => id.startsWith("fdc-");

export const parseBackendFoodId = (id: string): number | null => {
  if (!isBackendFoodId(id)) {
    return null;
  }

  const raw = Number(id.slice(4));
  return Number.isInteger(raw) && raw > 0 ? raw : null;
};

export const backendFoodToFoodItem = (backendFood: BackendFood): FoodItem => {
  const food: FoodItem = {
    id: backendFood.id,
    name: backendFood.name,
    brand: backendFood.brand ?? undefined,
    description: backendFood.description ?? undefined,
    servingSizes: backendFood.servingSizes.map((x, index) => ({
      label: x.name ?? '',
      grams: x.weight,
      id: `${backendFood.id}-serving-${index}`,
      isWeightBased: x.isWeightBased,
      nutrition: {
        calories: toNumber(x.nutrition.calories),
        protein: toNumber(x.nutrition.protein),
        carbs: toNumber(x.nutrition.carbs),
        fat: toNumber(x.nutrition.fat),
        labelNutrients: extractLabelNutrients(x.nutrition),
      },
    })),
    isCustom: false,
    isProvisional: !backendFood.isVerified,
    createdAt: backendFood.createdAt,
  };
  return food;
};

/**
 * Preserve the lightweight backend search result shape for screens that need metadata like kind.
 */
export const backendFoodSearchResultToSearchItem = (result: FoodSearchResult): FoodSearchResult => ({
  id: result.id,
  name: result.name,
  brand: result.brand,
  kind: result.kind,
  calories: toNumber(result.calories),
  protein: toNumber(result.protein),
  carbs: toNumber(result.carbs),
  fat: toNumber(result.fat),
  servingName: result.servingName || '1 serving',
});

const searchItemToFoodItem = (searchItem: FoodSearchResult): FoodItem => {
  return {
    id: searchItem.id,
    name: searchItem.name,
    brand: searchItem.brand ?? undefined,
    servingSizes: [{
      id: 'search-primary',
      label: searchItem.servingName,
      grams: null,
      nutrition: {
        calories: searchItem.calories,
        protein: searchItem.protein,
        carbs: searchItem.carbs,
        fat: searchItem.fat,
      },
    }],
    isCustom: false,
    createdAt: '',
  };
};

const hasUsableServingData = (food: FoodItem): boolean =>
  food.servingSizes.some(
    (serving) =>
      typeof serving.grams === 'number' &&
      Number.isFinite(serving.grams) &&
      serving.grams > 0,
  );

const hasAnyLabelNutrients = (food: FoodItem): boolean =>
  food.servingSizes.some((serving) => {
    const labelNutrients = serving.nutrition.labelNutrients;
    if (!labelNutrients || typeof labelNutrients !== 'object') {
      return false;
    }

    return Object.values(labelNutrients).some(
      (value) => typeof value === 'number' && Number.isFinite(value),
    );
  });

const hasInvalidServingIds = (food: FoodItem): boolean =>
  food.servingSizes.some((serving, index, servings) => {
    if (!serving.id) {
      return true;
    }

    return servings.findIndex((candidate) => candidate.id === serving.id) !== index;
  });

/**
 * Determine whether a system food should be hydrated with full backend details
 * before entering detail screens.
 */
export const shouldHydrateSystemFood = (food: FoodItem): boolean => {
  if (food.isCustom) {
    return false;
  }

  if (food.servingSizes.length === 0) {
    return true;
  }

  if (food.servingSizes.some((serving) => serving.id === 'search-primary')) {
    return true;
  }

  if (hasInvalidServingIds(food)) {
    return true;
  }

  if (!hasAnyLabelNutrients(food)) {
    return true;
  }

  return !hasUsableServingData(food);
};

export const searchBackendFoodResults = async (
  query: string,
  options: {
    foodType?: "all" | "staple" | "branded";
    pageSize?: number;
    pageNumber?: number;
  } = {},
): Promise<FoodSearchResult[]> => {
  const trimmed = query.trim();
  if (!trimmed) {
    return [];
  }

  try {
    const foodType = options.foodType ?? "all";
    const response = await client.search.$get({
      query: {
        q: trimmed,
        foodType: foodType,
        pageSize: options.pageSize ?? 25,
        pageNumber: options.pageNumber ?? 1,
      },
    });

    if (response.status !== 200) {
      return [];
    }

    const json = await response.json();
    if (!Array.isArray(json.foods)) {
      return [];
    }

    return json.foods.map((row) => backendFoodSearchResultToSearchItem(row));
  } catch {
    return [];
  }
};

export const searchBackendFoods = async (
  query: string,
  options: {
    foodType?: "all" | "staple" | "branded";
    pageSize?: number;
    pageNumber?: number;
  } = {},
): Promise<FoodItem[]> => {
  const results = await searchBackendFoodResults(query, options);
  return results.map(searchItemToFoodItem);
};

/**
 * Fetch full system food details by ID from the app DB
 */
export const fetchSystemFoodById = async (id: string): Promise<FoodItem | null> => {
  try {
    const response = await client.system[':id'].$get({
      param: { id },
    });

    if (response.status !== 200) {
      return null;
    }

    const json = await response.json();
    if (!json.food) {
      return null;
    }

    return backendFoodToFoodItem(json.food as BackendFood);
  } catch {
    return null;
  }
};
